import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  HelpCircle,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { COUNTRIES, STEPS } from './constants';
import type { CountryOption, StepDefinition } from './constants';
import type { FieldError } from './types';

export function Stepper({
  current,
  steps = STEPS,
  className,
}: {
  current: number;
  steps?: readonly StepDefinition[];
  className?: string;
}) {
  return (
    <div className={cn('w-full mb-6', className)}>
      <div className="flex items-center justify-between relative px-4">
        <div className="absolute left-8 right-8 top-5 h-1 bg-muted rounded-full -z-0">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${((current - 1) / (steps.length - 1)) * 100}%` }}
          />
        </div>

        {steps.map((step) => {
          const isCompleted = current > step.id;
          const isActive = current === step.id;
          const Icon = step.icon;

          return (
            <div key={step.id} className="relative z-10 flex flex-col items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2',
                  isCompleted
                    ? 'bg-primary border-primary text-primary-foreground shadow-soft'
                    : isActive
                    ? 'bg-card border-primary text-primary shadow-glow ring-4 ring-primary/10'
                    : 'bg-card border-muted text-muted-foreground',
                )}
              >
                {isCompleted ? (
                  <Check size={20} strokeWidth={3} />
                ) : (
                  <span className="text-sm font-semibold">{step.id}</span>
                )}
              </div>
              <span
                className={cn(
                  'text-xs font-semibold uppercase tracking-wider',
                  isActive || isCompleted ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {step.short}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FieldShellProps {
  id: string;
  label: string;
  hint?: string;
  tooltip?: string;
  error?: FieldError;
  optional?: boolean;
  required?: boolean;
  isValid?: boolean;
  children: ReactNode;
  className?: string;
}

export function FieldShell({
  id,
  label,
  hint,
  tooltip,
  error,
  optional,
  required,
  isValid,
  children,
  className,
}: FieldShellProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label htmlFor={id} className="text-[15px] font-medium text-foreground">
            {label}
            {required && (
              <span className="ml-1 text-red-600" aria-hidden>
                *
              </span>
            )}
            {optional && (
              <span className="ml-2 text-[13px] font-normal text-muted-foreground">(Optional)</span>
            )}
          </Label>
          {tooltip && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="More information"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isValid && !error && (
          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-emerald-600" aria-hidden>
            <CheckCircle2 className="h-4 w-4" />
          </span>
        )}
      </div>
      {children}
      {error ? (
        <p role="alert" className="flex items-start gap-1.5 text-[13px] text-red-600">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function CountryCombobox({
  value,
  onChange,
  disabled,
  invalid,
  options = COUNTRIES,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  options?: readonly CountryOption[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((c) => c.name.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          'flex h-12 w-full items-center justify-between rounded-md border bg-card px-3 text-[15px] text-foreground shadow-sm transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring/30',
          invalid ? 'border-red-400' : 'border-border hover:border-border',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={cn('truncate', !value && 'text-muted-foreground')}>
          {value || 'Select a country'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          <div className="flex items-center gap-2 border-b border-border/60 px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country"
              className="h-11 flex-1 bg-card text-[15px] text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-[14px] text-muted-foreground">No matches</li>
            ) : (
              filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c.name);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2.5 text-left text-[14px] text-foreground hover:bg-muted',
                      value === c.name && 'bg-muted font-medium',
                    )}
                  >
                    <span>{c.name}</span>
                    <span className="text-[13px] text-muted-foreground">{c.dial}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function GuidancePanel({
  title,
  children,
  icon: Icon = ShieldCheck,
}: {
  title: string;
  children: ReactNode;
  icon?: typeof ShieldCheck;
}) {
  return (
    <aside className="rounded-xl border border-border bg-muted/40 p-5">
      <div className="mb-3 flex items-center gap-2 text-foreground">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-card shadow-sm">
          <Icon className="h-4 w-4 text-foreground/85" />
        </span>
        <h3 className="text-[15px] font-semibold">{title}</h3>
      </div>
      <div className="space-y-2 text-[14px] leading-relaxed text-foreground/80">{children}</div>
    </aside>
  );
}

export function SummaryCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="flex items-center justify-between border-b border-border/60 px-6 py-4">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-[13px] font-medium text-foreground/80 underline-offset-4 hover:text-foreground hover:underline"
        >
          Edit
        </button>
      </header>
      <div className="space-y-3 px-6 py-5">{children}</div>
    </section>
  );
}

export function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-1 md:grid-cols-[180px_1fr] md:items-baseline md:gap-4">
      <dt className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'text-[14px] leading-relaxed text-foreground',
          mono && 'font-mono text-[13px]',
          (!value || value === '-') && 'text-muted-foreground',
        )}
      >
        {value || '-'}
      </dd>
    </div>
  );
}
